// Archivo: src/pages/AdminDashboard.js

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../components/RichTextContent.css";
import { getAllServices } from "../services/admin.service";
import {
  deleteService,
  restoreService,
  destroyServicePermanently,
} from "../services/admin.service";
import { useAuth } from "../services/AuthContext";

// Iconos reutilizables
const ServiceIcon = () => (
  <svg
    className="w-8 h-8"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

const EditIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const SectionsIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const ViewIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
    />
  </svg>
);

const PauseIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M6 4v16M18 4v16"
    />
  </svg>
);

const RestoreIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

const TrashIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const STORAGE_URL = process.env.REACT_APP_STORAGE_URL;

// Componente de tarjeta de servicio
const ServiceCard = ({
  service,
  onDeleteClick,
  onRestoreClick,
  onDestroyClick,
}) => (
  <div
    className={`bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 ${service.deleted_at ? "opacity-60 bg-gray-50" : ""}`}
  >
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          {service.logo_path ? (
            <img
              src={`${STORAGE_URL}/${service.logo_path}`}
              alt={service.name}
              className="w-12 h-12 rounded-lg object-contain bg-gray-50 p-1 border border-gray-200"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextElementSibling.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className={`w-12 h-12 bg-gob-azul-medio rounded-lg flex items-center justify-center text-white ${
              service.logo_path ? "hidden" : "flex"
            }`}
          >
            <ServiceIcon />
          </div>
          <div>
            <div className="flex items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                {service.name}
              </h3>
              {service.deleted_at && (
                <span className="ml-3 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                  Desactivado
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">ID: {service.id}</p>
          </div>
        </div>
      </div>

      <div
        className="text-gray-600 text-sm mb-6 line-clamp-3 h-20 rich-text-content"
        dangerouslySetInnerHTML={{
          __html: service.description || "Sin descripción disponible",
        }}
      />

      {/* Acciones (responsive: envuelven cuando no hay espacio) */}
      <div className="flex flex-wrap gap-3">
        {service.deleted_at ? (
          <button
            type="button"
            disabled
            className="inline-flex w-full sm:w-auto items-center justify-center px-3 py-2 bg-gray-100 text-gray-500 rounded-lg transition-colors text-sm font-medium cursor-not-allowed opacity-60"
          >
            <EditIcon />
            <span className="ml-2">Editar</span>
          </button>
        ) : (
          <Link
            to={`/admin/servicios/${service.id}/editar`}
            className="inline-flex w-full sm:w-auto items-center justify-center px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
          >
            <EditIcon />
            <span className="ml-2">Editar</span>
          </Link>
        )}

        {service.deleted_at ? (
          <button
            type="button"
            disabled
            className="inline-flex w-full sm:w-auto items-center justify-center px-3 py-2 bg-gray-100 text-gray-500 rounded-lg transition-colors text-sm font-medium cursor-not-allowed opacity-60"
          >
            <SectionsIcon />
            <span className="ml-2">Secciones</span>
          </button>
        ) : (
          <Link
            to={`/admin/servicios/${service.id}/secciones`}
            className="inline-flex w-full sm:w-auto items-center justify-center px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
          >
            <SectionsIcon />
            <span className="ml-2">Secciones</span>
          </Link>
        )}

        {service.deleted_at ? (
          <button
            type="button"
            disabled
            className="inline-flex w-full sm:w-auto items-center justify-center px-3 py-2 bg-gray-100 text-gray-500 rounded-lg transition-colors text-sm font-medium cursor-not-allowed opacity-60"
          >
            <ViewIcon />
            <span className="ml-2">Ver</span>
          </button>
        ) : (
          <Link
            to={`/servicios/${service.id}`}
            target="_blank"
            className="inline-flex w-full sm:w-auto items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
          >
            <ViewIcon />
            <span className="ml-2">Ver</span>
          </Link>
        )}

        {/* Botones de gestión: desactivar / restaurar / eliminar permanente (Links para mantener diseño) */}
        {!service.deleted_at ? (
          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              onDeleteClick(service);
            }}
            className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm font-medium"
          >
            <PauseIcon />
            <span className="ml-2">Desactivar</span>
          </Link>
        ) : (
          <>
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                onRestoreClick(service);
              }}
              className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
            >
              <RestoreIcon />
              <span className="ml-2">Restaurar</span>
            </Link>
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                onDestroyClick(service);
              }}
              className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
            >
              <TrashIcon />
              <span className="ml-2">Eliminar</span>
            </Link>
          </>
        )}
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [modalService, setModalService] = useState(null);
  const [modalMode, setModalMode] = useState(null); // 'delete' | 'restore' | 'destroy'
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await getAllServices();
        setServices(data);
      } catch (error) {
        console.error("Error fetching services:", error);
        setError("Error al cargar los servicios");
      } finally {
        setIsLoading(false);
      }
    };

    fetchServices();
  }, []);

  const reloadServices = async () => {
    setIsLoading(true);
    try {
      const data = await getAllServices();
      setServices(data);
    } catch (err) {
      console.error("Error reloading services:", err);
      setError("Error al recargar los servicios");
    } finally {
      setIsLoading(false);
    }
  };

  // Abrir modal con modo y servicio
  const openModal = (service, mode) => {
    setModalService(service);
    setModalMode(mode);
    setConfirmationText("");
  };

  const closeModal = () => {
    setModalService(null);
    setModalMode(null);
    setConfirmationText("");
  };

  const performAction = async () => {
    if (!modalService || !modalMode) return;
    setIsProcessing(true);
    setError(null);
    try {
      if (modalMode === "delete") {
        await deleteService(modalService.id);
        setSuccessMessage("Servicio desactivado exitosamente");
      } else if (modalMode === "restore") {
        await restoreService(modalService.id);
        setSuccessMessage("Servicio restaurado exitosamente");
      } else if (modalMode === "destroy") {
        // Verificar confirmación exacta
        if (confirmationText.trim() !== modalService.name) {
          setError("Escribe el nombre exacto del servicio para confirmar");
          setIsProcessing(false);
          return;
        }
        await destroyServicePermanently(modalService.id);
        setSuccessMessage("Servicio eliminado permanentemente");
      }

      closeModal();
      await reloadServices();
    } catch (err) {
      console.error("Action error:", err);
      setError("Error al realizar la acción: " + (err.message || ""));
    } finally {
      setIsProcessing(false);
      // limpiar mensajes tras 4s
      setTimeout(() => {
        setSuccessMessage("");
        setError(null);
      }, 4000);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 bg-gray-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-red-800 mb-2">Error</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Panel de Administración
        </h1>
        <p className="text-gray-600">
          Bienvenido, {user?.first_name} {user?.last_name}. Gestiona los
          servicios y su contenido.
        </p>
      </div>

      {/* Estadísticas rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <ServiceIcon />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Total Servicios
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {services.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <SectionsIcon />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Servicios Activos
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {services.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <ViewIcon />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">En gestión</p>
              <p className="text-2xl font-bold text-gray-900">
                {services.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de servicios */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Servicios</h2>
          <span className="text-sm text-gray-500">
            {services.length} servicios encontrados
          </span>
        </div>
        <div className="flex justify-end mb-6">
          <Link
            to="/admin/servicios/nuevo"
            className="inline-flex items-center px-4 py-2 bg-gob-azul-medio text-white rounded-lg hover:bg-gob-azul-oscuro transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span className="ml-2">Nuevo servicio</span>
          </Link>
        </div>

        {services.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onDeleteClick={(s) => openModal(s, "delete")}
                onRestoreClick={(s) => openModal(s, "restore")}
                onDestroyClick={(s) => openModal(s, "destroy")}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <ServiceIcon />
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              No hay servicios
            </h3>
            <p className="mt-1 text-gray-500">
              Los servicios aparecerán aquí cuando estén disponibles.
            </p>
          </div>
        )}
      </div>

      {/* Modal de confirmación para acciones de servicio */}
      {modalService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {modalMode === "delete" && "Desactivar servicio"}
              {modalMode === "restore" && "Restaurar servicio"}
              {modalMode === "destroy" && "Eliminar permanentemente"}
            </h3>

            <div
              className={`p-4 rounded-lg mb-4 ${modalMode === "destroy" ? "bg-red-50 border border-red-200" : modalMode === "delete" ? "bg-yellow-50 border border-yellow-200" : "bg-green-50 border border-green-200"}`}
            >
              <p
                className={`font-medium mb-2 ${modalMode === "destroy" ? "text-red-800" : modalMode === "delete" ? "text-yellow-800" : "text-green-800"}`}
              >
                {modalMode === "destroy"
                  ? "⚠️ ACCIÓN IRREVERSIBLE"
                  : modalMode === "delete"
                    ? "⚠️ ACCIÓN DE DESACTIVACIÓN"
                    : "Restaurar servicio"}
              </p>
              <p
                className={
                  modalMode === "destroy"
                    ? "text-red-700"
                    : modalMode === "delete"
                      ? "text-yellow-700"
                      : "text-green-700"
                }
              >
                {modalMode === "destroy"
                  ? "Esta acción eliminará permanentemente el servicio y todos sus datos asociados."
                  : modalMode === "delete"
                    ? "Esta acción desactivará el servicio. Podrás restaurarlo más tarde."
                    : "Esta acción restaurará el servicio desactivado."}
              </p>
            </div>

            <p className="text-gray-600 mb-4">
              Servicio: <span className="font-medium">{modalService.name}</span>
            </p>

            {modalMode === "destroy" && (
              <div className="mb-4">
                <p className="text-sm text-gray-700 mb-2">
                  Escribe el nombre exacto del servicio para confirmar:
                </p>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                {confirmationText && confirmationText !== modalService.name && (
                  <p className="text-red-600 text-sm mt-2">
                    El texto no coincide exactamente
                  </p>
                )}
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeModal}
                disabled={isProcessing}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                onClick={performAction}
                disabled={
                  isProcessing ||
                  (modalMode === "destroy" &&
                    confirmationText.trim() !== modalService.name)
                }
                className={`px-4 py-2 rounded-lg text-white ${modalMode === "destroy" ? "bg-red-600 hover:bg-red-700" : modalMode === "delete" ? "bg-yellow-600 hover:bg-yellow-700" : "bg-green-600 hover:bg-green-700"} disabled:opacity-50`}
              >
                {isProcessing
                  ? "Procesando..."
                  : modalMode === "destroy"
                    ? "Eliminar permanentemente"
                    : modalMode === "delete"
                      ? "Desactivar"
                      : "Restaurar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="fixed bottom-6 right-6 bg-green-50 border border-green-200 p-3 rounded-lg shadow">
          <p className="text-green-700">{successMessage}</p>
        </div>
      )}
    </div>
  );
}
